import { TierName, TierStatusName } from '@order/graphql';
import {
  DateDurationUnit,
  type useLocalizationContext,
} from '@order/Localization';

import type { AccountSweetpassMembershipSection } from '../../useSweetpassAccountContentfulData';
import type {
  AsyncCta,
  GetSectionProps,
  SweetpassSectionProps,
} from '../types';
import { convertAndFormatDate } from './common';

// ─────────────────────────────────────────────────────────────────────────────

export const getMembershipLevelSection = (
  props: GetMembershipLevelSectionProps,
): SweetpassMembershipLevelSectionProps | undefined => {
  const {
    tier,
    tierStatus,
    hasPaidTierHistory,
    joinDate,
    activeSubscription,
    shouldUseIsoDate,
  } = props;

  const { formattedDate: formattedJoinDate } = convertAndFormatDate(
    shouldUseIsoDate,
    joinDate ?? '',
  );

  const contentProps = {
    ...props,
    joinDate: formattedJoinDate ?? '',
  };

  const hasActiveSubscription = Boolean(activeSubscription);
  const isSweetpass =
    (tier?.name === TierName.SweetpassPlus && !hasActiveSubscription) ||
    tier?.name === TierName.Sweetpass;
  const isSweetpassPlus =
    hasActiveSubscription && tier?.name === TierName.SweetpassPlus;
  const isSweetpassPlusWithFreeTrial =
    isSweetpassPlus && tierStatus === TierStatusName.FreeTrial;

  const isDowngradedSweetpass = isSweetpass && hasPaidTierHistory;
  const isCancelledSweetpassPlus =
    isSweetpassPlus && tierStatus === TierStatusName.CancellationPending;

  if (isCancelledSweetpassPlus) {
    return getMembershipLevelSectionContentForSweetpassPlusCanceled(
      contentProps,
    );
  }

  if (isSweetpassPlusWithFreeTrial) {
    return getMembershipLevelSectionContentForSweetpassPlusTrial(contentProps);
  }

  if (isSweetpassPlus) {
    return getMembershipLevelSectionContentForSweetpassPlusActive(contentProps);
  }

  if (isDowngradedSweetpass) {
    return getMembershipLevelSectionContentForSweetpassDowngraded(contentProps);
  }

  if (isSweetpass) {
    return getMembershipLevelSectionContentForSweetpassInitial(contentProps);
  }
};

// ─── Tier: Sweetpass (Initial) ───────────────────────────────────────────────

const getMembershipLevelSectionContentForSweetpassInitial = (
  props: GetMembershipLevelSectionContentProps,
): SweetpassMembershipLevelSectionProps | undefined => {
  const {
    name,
    membershipDataTitle,
    membershipDataHeader,
    membershipDataBodySweetpass,
    membershipDataCtaSweetpass,
    membershipDataCtaSweetpassFreeTrial,
    planPrice,
    billingFrequencyUnit,
    joinDate,
    isEligibleToFreeTrial,
    freeTrialPlanId = '',
    subscribeHandler,
  } = props;

  const cta = isEligibleToFreeTrial
    ? membershipDataCtaSweetpassFreeTrial
    : membershipDataCtaSweetpass;

  const handler = async () => {
    const planId: string | undefined = isEligibleToFreeTrial
      ? freeTrialPlanId
      : undefined;

    return subscribeHandler(planId);
  };

  return {
    name,
    title: membershipDataTitle,
    headingText: membershipDataHeader.replaceAll(
      '{membershipLevel}',
      MEMBERSHIP_SWEETPASS,
    ),
    bodyText: joinDate
      ? membershipDataBodySweetpass.replaceAll('{joinDate}', joinDate)
      : '',
    cta: {
      palette: 'limeOnGreen',
      title: cta
        .replaceAll('{planPrice}', `${planPrice}`)
        .replaceAll(
          '{billingFrequencyUnit}',
          billingFrequencyUnit?.toLowerCase(),
        ),
      accessibilityLabel: cta,
      handler,
    },
  };
};

// ─── Tier: Sweetpass (Downgraded) ────────────────────────────────────────────

const getMembershipLevelSectionContentForSweetpassDowngraded = (
  props: GetMembershipLevelSectionContentProps,
): SweetpassMembershipLevelSectionProps | undefined => {
  const {
    name,
    membershipDataTitle,
    membershipDataHeader,
    membershipDataCtaSweetpassFreeTrial,
    membershipDataCtaSweetpassDowngraded,
    planPrice,
    billingFrequencyUnit,
    isEligibleToFreeTrial,
    freeTrialPlanId = '',
    subscribeHandler,
  } = props;

  const cta = isEligibleToFreeTrial
    ? membershipDataCtaSweetpassFreeTrial
    : membershipDataCtaSweetpassDowngraded;

  const handler = async () => {
    const planId: string | undefined = isEligibleToFreeTrial
      ? freeTrialPlanId
      : undefined;

    return subscribeHandler(planId);
  };

  return {
    name,
    title: membershipDataTitle,
    headingText: membershipDataHeader.replaceAll(
      '{membershipLevel}',
      MEMBERSHIP_SWEETPASS,
    ),
    // if there is ever a requirement for a sweetpass downgraded body text, use this.
    // bodyText: membershipDataBodySweetpassDowngraded,
    cta: {
      palette: isEligibleToFreeTrial ? 'limeOnGreen' : 'secondary',
      title: cta
        .replaceAll('{planPrice}', `${planPrice}`)
        .replaceAll(
          '{billingFrequencyUnit}',
          billingFrequencyUnit?.toLowerCase(),
        ),
      accessibilityLabel: cta,
      handler,
    },
  };
};

// ─── Tier: Sweetpass+ (Active) ───────────────────────────────────────────────

const getMembershipLevelSectionContentForSweetpassPlusActive = (
  props: GetMembershipLevelSectionContentProps,
): SweetpassMembershipLevelSectionProps | undefined => {
  const {
    name,
    membershipDataTitle,
    membershipDataHeader,
    membershipDataBodySweetpassPlus,
    membershipDataBodySweetpassPlusSwitchSubscription,
    membershipDataCtaSwitchSubscriptionTitle,
    membershipDataCtaSwitchSubscriptionBackTitle,
    membershipDataCtaSwitchSubscriptionA11y,
    activeSubscription,
    shouldUseIsoDate,
    switchSubscriptionPrice = '',
    isSubscriptionAnnual,
    hasPendingSubscription,
    canSwitchSubscription,
    switchSubscriptionHandler,
    formatMessage,
    formatDurationUnitName,
  } = props;

  if (!activeSubscription) return undefined;

  const { nextBillingDate = '', nextBillingPeriodAmount = '' } =
    activeSubscription;

  const { formattedDate: formattedNextBillingDate } = convertAndFormatDate(
    shouldUseIsoDate,
    nextBillingDate,
  );

  const changeTo = isSubscriptionAnnual
    ? formatMessage('account.sweetpass-membership.membership-level.monthly')
    : formatMessage('account.sweetpass-membership.membership-level.annual');

  const planInfo = isSubscriptionAnnual
    ? formatMessage('account.sweetpass-membership.membership-level.annual-plan')
    : formatMessage(
        'account.sweetpass-membership.membership-level.monthly-plan',
      );

  const nextBillingPeriodRecurrence = formatDurationUnitName(
    (isSubscriptionAnnual && !hasPendingSubscription) ||
      (!isSubscriptionAnnual && hasPendingSubscription)
      ? DateDurationUnit.Year
      : DateDurationUnit.Month,
  );

  if (!formattedNextBillingDate) return undefined;

  return {
    name,
    title: membershipDataTitle,
    headingText: membershipDataHeader.replaceAll(
      '{membershipLevel}',
      MEMBERSHIP_SWEETPASS_PLUS,
    ),
    planInfo: canSwitchSubscription ? planInfo : undefined,
    bodyText:
      canSwitchSubscription && hasPendingSubscription
        ? membershipDataBodySweetpassPlusSwitchSubscription
            .replaceAll('{changeTo}', changeTo)
            .replaceAll('{nextBillingDate}', formattedNextBillingDate)
            .replaceAll('{nextBillingPeriodAmount}', switchSubscriptionPrice)
            .replaceAll(
              '{nextBillingPeriodRecurrence}',
              nextBillingPeriodRecurrence,
            )
        : membershipDataBodySweetpassPlus
            .replaceAll('{nextBillingDate}', formattedNextBillingDate)
            .replaceAll('{nextBillingPeriodAmount}', nextBillingPeriodAmount)
            .replaceAll(
              '{nextBillingPeriodRecurrence}',
              nextBillingPeriodRecurrence,
            ),
    ctaSwitchSubscription: canSwitchSubscription
      ? {
          title: hasPendingSubscription
            ? membershipDataCtaSwitchSubscriptionBackTitle
            : membershipDataCtaSwitchSubscriptionTitle,
          accessibilityLabel: membershipDataCtaSwitchSubscriptionA11y,
          handler: switchSubscriptionHandler,
        }
      : undefined,
  };
};

// ─── Tier: Sweetpass+ (Canceled) ─────────────────────────────────────────────

const getMembershipLevelSectionContentForSweetpassPlusCanceled = (
  props: GetMembershipLevelSectionContentProps,
): SweetpassMembershipLevelSectionProps | undefined => {
  const {
    name,
    membershipDataTitle,
    membershipDataHeader,
    membershipDataBodySweetpassPlusCancelled,
    membershipDataCtaSweetpassPlusCancelled,
    activeSubscription,
    shouldUseIsoDate,
    reactivateSubscriptionHandler,
  } = props;

  if (!activeSubscription) return undefined;

  const { paidThroughDate } = activeSubscription;

  const { formattedDate: formattedPaidThroughDate } = convertAndFormatDate(
    shouldUseIsoDate,
    paidThroughDate ?? '',
  );

  return {
    name,
    title: membershipDataTitle,
    headingText: membershipDataHeader.replaceAll(
      '{membershipLevel}',
      MEMBERSHIP_SWEETPASS,
    ),
    bodyText: formattedPaidThroughDate
      ? membershipDataBodySweetpassPlusCancelled.replaceAll(
          '{paidThroughDate}',
          formattedPaidThroughDate,
        )
      : undefined,
    cta: {
      palette: 'secondary',
      title: membershipDataCtaSweetpassPlusCancelled,
      accessibilityLabel: membershipDataCtaSweetpassPlusCancelled,
      handler: reactivateSubscriptionHandler,
    },
  };
};

// ─── Tier: Sweetpass+ (Free Trial) ───────────────────────────────────────────

export const getMembershipLevelSectionContentForSweetpassPlusTrial = (
  params: GetMembershipLevelSectionContentForSweetpassPlusTrialProps,
): SweetpassMembershipLevelSectionProps | undefined => {
  const {
    name,
    membershipDataTitle: title,
    membershipDataHeader,
    membershipDataBodySweetpassPlusFreeTrial,
    membershipDataCtaSwitchSubscriptionTitle,
    membershipDataCtaSwitchSubscriptionA11y,
    activeSubscription,
    shouldUseIsoDate,
    canSwitchSubscription,
    isSubscriptionAnnual,
    formatMessage,
    formatDurationUnitName,
    formatDistanceBetweenDatesInDays,
    switchSubscriptionHandler,
  } = params;

  if (!activeSubscription) return;

  // ─────────────────────────────────────────────────────────────────────

  const headingText = membershipDataHeader.replaceAll(
    '{membershipLevel}',
    formatMessage(
      'account.sweetpass-membership.membership-level.sweetpass-plus-free-trial.name',
    ),
  );

  const planInfo = isSubscriptionAnnual
    ? formatMessage('account.sweetpass-membership.membership-level.annual-plan')
    : formatMessage(
        'account.sweetpass-membership.membership-level.monthly-plan',
      );

  // ─────────────────────────────────────────────────────────────────────

  const { price, firstBillingDate } = activeSubscription;
  const billingFrequencyUnit = formatDurationUnitName(
    isSubscriptionAnnual ? DateDurationUnit.Year : DateDurationUnit.Month,
  );

  const currentDate = new Date();

  const ctaSwitchSubscription = canSwitchSubscription
    ? {
        palette: 'secondary' as const,
        title: membershipDataCtaSwitchSubscriptionTitle,
        accessibilityLabel: membershipDataCtaSwitchSubscriptionA11y,
        handler: switchSubscriptionHandler,
      }
    : undefined;

  const { dateWithoutTimezone, formattedDate: formattedFirstBillingDate } =
    convertAndFormatDate(shouldUseIsoDate, firstBillingDate ?? '');

  if (price && formattedFirstBillingDate) {
    const remainingTime = formatDistanceBetweenDatesInDays({
      baseDate: currentDate,
      targetDate: dateWithoutTimezone,
      withoutPrefix: true,
      useMidnight: true,
      minDays: 1,
    });

    const bodyText = membershipDataBodySweetpassPlusFreeTrial
      .replaceAll('{timeRemaining}', remainingTime)
      .replaceAll('{price}', price)
      .replaceAll('{billingFrequencyUnit}', billingFrequencyUnit)
      .replaceAll('{nextBillingDate}', formattedFirstBillingDate);

    return {
      name,
      title,
      planInfo,
      headingText,
      bodyText,
      ctaSwitchSubscription,
    };
  }

  // ─────────────────────────────────────────────────────────────────────

  return {
    name,
    title,
    headingText,
    ctaSwitchSubscription,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const MEMBERSHIP_SWEETPASS = 'Sweetpass';
const MEMBERSHIP_SWEETPASS_PLUS = 'Sweetpass+';

// ─── Types ───────────────────────────────────────────────────────────────────

type SweetpassMembershipLevelCta = AsyncCta;
type SweetpassChangSubscriptionCta = {
  title: string;
  accessibilityLabel: string;
  handler: () => void;
};

type GetMembershipLevelSectionProps = Readonly<{
  shouldUseIsoDate: boolean;
  canSwitchSubscription: boolean;
  autoRenewInfo?: string;
  subscribeHandler: (planId?: string) => Promise<void>;
  reactivateSubscriptionHandler: () => Promise<void>;
  switchSubscriptionHandler: () => void;
  formatDurationUnitName: ReturnType<
    typeof useLocalizationContext
  >['formatDurationUnitName'];
  formatDistanceBetweenDatesInDays: ReturnType<
    typeof useLocalizationContext
  >['formatDistanceBetweenDatesInDays'];
}> &
  AccountSweetpassMembershipSection &
  GetSectionProps;

type GetMembershipLevelSectionContentProps = GetMembershipLevelSectionProps &
  AccountSweetpassMembershipSection &
  Readonly<{
    joinDate: string | null;
  }>;

type GetMembershipLevelSectionContentForSweetpassPlusTrialProps = Pick<
  GetMembershipLevelSectionContentProps,
  | 'name'
  | 'membershipDataTitle'
  | 'membershipDataHeader'
  | 'membershipDataBodySweetpassPlusFreeTrial'
  | 'membershipDataCtaSwitchSubscriptionTitle'
  | 'membershipDataCtaSwitchSubscriptionA11y'
  | 'activeSubscription'
  | 'formatMessage'
  | 'formatDurationUnitName'
  | 'formatDistanceBetweenDatesInDays'
  | 'shouldUseIsoDate'
  | 'canSwitchSubscription'
  | 'switchSubscriptionHandler'
  | 'isSubscriptionAnnual'
>;

export type SweetpassMembershipLevelSectionProps = Readonly<{
  name: string;
  title: string;
  planInfo?: string;
  headingText?: string;
  bodyText?: string;
  ctaSwitchSubscription?: SweetpassChangSubscriptionCta;
  cta?: SweetpassMembershipLevelCta;
  isSwitchSubscriptionCtaLoading?: boolean;
}> &
  SweetpassSectionProps;
